import type { FC } from 'react';
import { useMemo, useState } from 'react';
import EmailConfirmPanel from './EmailConfirmPanel';
import SidePopup from '../../../../../shared/SidePopup/SidePopup';
import PersonalDataPopupContent from './popups/PersonalDataPopupContent';
import DeliveryAddressPopupContent from './popups/DeliveryAddressPopupContent';
import type { UserProfile } from './popups/userData.types';
import styles from './MyDataPanel.module.scss';

type Props = {
    user: UserProfile;
};
type AddressItem = {
    id: string;
    raw: string;
    struct?: any | null;
    comment?: string;
    isPrimary: boolean;
    canEdit: boolean;
};

const digitsOnly = (v: string): string => v.replace(/\D/g, '');
const formatRuPhonePretty = (rawPhone: string): string => {
    const raw = String(rawPhone || '').trim();
    if (!raw) return '';
    const digits = digitsOnly(raw);
    // нормализуем к национальным 10 цифрам РФ
    let national10 = '';
    // +7XXXXXXXXXX или 7XXXXXXXXXX
    if (digits.length === 11 && digits.startsWith('7')) national10 = digits.slice(1);
    // 8XXXXXXXXXX
    else if (digits.length === 11 && digits.startsWith('8')) national10 = digits.slice(1);
    // XXXXXXXXXX
    else if (digits.length === 10) national10 = digits;
    if (national10.length !== 10) return raw; // не ломаем неизвестные форматы
    const a = national10.slice(0, 3);
    const b = national10.slice(3, 6);
    const c = national10.slice(6, 8);
    const d = national10.slice(8, 10);
    return `+7 (${a}) ${b}-${c}-${d}`;
};

const MyDataPanel: FC<Props> = ({ user }) => {
    const [personalPopupOpen, setPersonalPopupOpen] = useState(false);
    const [addressPopupOpen, setAddressPopupOpen] = useState(false);
    const [addressPopupMode, setAddressPopupMode] = useState<'edit' | 'add'>('edit');
    const [editingAddress, setEditingAddress] = useState<AddressItem | null>(null);

    const fullName = useMemo(() => {
        const parts = [user?.firstName, user?.lastName].filter(Boolean);
        return parts.join(' ').trim();
    }, [user]);

    const dob = useMemo(() => {
        const v = user?.dateOfBirth?.date || '';
        return v ? String(v) : '';
    }, [user]);

    const phone = useMemo(() => formatRuPhonePretty(String(user?.phoneNumber || '')), [user]);
    const email = useMemo(() => String(user?.email || '').trim(), [user]);

    const addresses: AddressItem[] = useMemo(() => {
        const list: AddressItem[] = [];
        const primaryRaw = String((user as any)?.address?.raw ?? '').trim();
        if (primaryRaw) {
            list.push({
                id: 'primary',
                raw: primaryRaw,
                struct: (user as any)?.address?.struct ?? null,
                comment: String((user as any)?.address?.comment ?? '').trim(),
                isPrimary: true,
                canEdit: true,
            });
        }
        const extra = Array.isArray((user as any)?.addresses) ? (user as any).addresses : [];
        for (let i = 0; i < extra.length; i += 1) {
            const a = extra[i];
            if (!a || (a as any).isDisabled) continue;
            const raw = String((a as any).raw ?? '').trim();
            if (!raw) continue;
            list.push({
                id: String((a as any)._id ?? (a as any).id ?? i),
                raw,
                struct: (a as any).struct ?? null,
                comment: String((a as any).comment ?? '').trim(),
                isPrimary: false,
                canEdit: true,
            });
        }
        return list;
    }, [user]);

    const primaryId = useMemo(() => addresses.find((a) => a.isPrimary)?.id ?? '', [addresses]);
    const hasPersonalAny = Boolean(fullName || dob || phone || email);

    const personalLines = useMemo(() => {
        const lines: string[] = [];
        if (fullName) lines.push(fullName);
        if (dob) lines.push(dob);
        if (phone) lines.push(phone);
        if (email) lines.push(email);
        return lines;
    }, [fullName, dob, phone, email]);

    const needEmailConfirm = Boolean(user?.email && !user?.emailStatus);

    const openPersonal = () => setPersonalPopupOpen(true);
    const closePersonal = () => setPersonalPopupOpen(false);

    const openAddressEdit = (a: AddressItem) => {
        setEditingAddress(a);
        setAddressPopupMode('edit');
        setAddressPopupOpen(true);
    };
    const openAddressAdd = () => {
        setEditingAddress(null);
        setAddressPopupMode('add');
        setAddressPopupOpen(true);
    };
    const closeAddress = () => setAddressPopupOpen(false);

    return (
        <div className={styles.wrapper}>
            <div className={styles.block}>
                <div className={styles.headRow}>
                    <h2 className={styles.title}>Личные данные</h2>
                    <button type="button" className={styles.editBtn} onClick={openPersonal} aria-label="Редактировать личные данные">
                        <img className={styles.editBtnIcon} src="/icons/new/edit.svg" alt="" aria-hidden="true" />
                        <span>Редактировать</span>
                    </button>
                </div>
                <div className={styles.lines} aria-label="Личные данные">
                    {!hasPersonalAny ? (
                        <div className={styles.muted}>Данные пока не заполнены.</div>
                    ) : (
                        personalLines.map((ln, i) => (
                            <div key={`${ln}-${i}`} className={styles.line}>
                                {ln}
                            </div>
                        ))
                    )}
                </div>
                {needEmailConfirm && (
                    <div className={styles.confirmBox}>
                        <EmailConfirmPanel user={user} />
                    </div>
                )}
            </div>
            <div className={styles.block}>
                <div className={styles.headRow}>
                    <h2 className={styles.title}>Адрес доставки</h2>
                </div>
                <div className={styles.addrList} role="radiogroup" aria-label="Список адресов доставки">
                    {addresses.map((a) => {
                        const isPrimary = a.id === primaryId;
                        return (
                            <div key={a.id} className={styles.addrItem} role="listitem">
                                <span
                                    className={`${styles.addrCheck}${isPrimary ? ` ${styles.addrCheckActive}` : ''}`}
                                    aria-hidden="true"
                                    title={isPrimary ? 'Основной адрес' : ''}
                                />
                                <div className={styles.addrMain}>
                                    <div className={styles.addrTextRow}>
                                        <span className={styles.addrText}>{a.raw}</span>
                                        {/* {isPrimary ? <span className={styles.primaryBadge}>Основной</span> : null} */}
                                    </div>
                                    {a.comment ? <div className={styles.addrComment}>{a.comment}</div> : null}
                                </div>
                                {a.canEdit ? (
                                    <button
                                        type="button"
                                        className={styles.addrEditInline}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            openAddressEdit(a);
                                        }}
                                    >
                                        <img className={styles.editBtnIcon} src="/icons/new/edit.svg" alt="" aria-hidden="true" />
                                        <span>Редактировать</span>
                                    </button>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
                <button
                    type="button"
                    className={styles.addAddrBtn}
                    onClick={(e) => {
                        e.preventDefault();
                        openAddressAdd();
                    }}
                >
                    Добавить адрес <span className={styles.plus}>+</span>
                </button>
            </div>
            <SidePopup open={personalPopupOpen} onClose={closePersonal} className="myDataPanel__personalSidePopup" showCloseButton={false}>
                <PersonalDataPopupContent user={user} onClose={closePersonal} />
            </SidePopup>
            <SidePopup open={addressPopupOpen} onClose={closeAddress} className="myDataPanel__addressSidePopup" showCloseButton={false}>
                <DeliveryAddressPopupContent user={user} mode={addressPopupMode} targetAddress={editingAddress} onClose={closeAddress} />
            </SidePopup>
        </div>
    );
};

export default MyDataPanel;
